import { Badge, BadgeProps, cn, TruncatedContent } from "@aurem/ui";
import { ReactElement } from "react";
import { NavLink } from "react-router-dom";
import { Avatar } from "../avatar";

type Props = {
  to?: string;
  icon?: ReactElement;
  label: string;
  name?: string;
  isActive?: boolean;
  badgeProps?: Omit<BadgeProps, "children">;
  onClick?: () => void;
};

export const PlatformSidebarItem = ({
  to,
  icon,
  label,
  name,
  isActive,
  badgeProps,
  onClick,
}: Props) => {
  const renderContent = (isActive: boolean) => (
    <>
      {name && <Avatar name={name} className="rounded-xl" />}
      {icon && (
        <span
          className={cn(
            "rounded-full p-3 transition-transform duration-200",
            isActive
              ? "bg-white/20"
              : "group-hover:bg-white/10 group-aria-pressed:bg-white/5",
          )}
        >
          {icon}
        </span>
      )}
      <TruncatedContent
        className="text-xs"
        tooltipProps={{ placement: "right" }}
        maxLines={2}
      >
        {label}
      </TruncatedContent>
    </>
  );

  const commonClasses = cn(
    "flex flex-col w-full items-center gap-1 rounded-md",
    "focus-visible:outline focus-visible:outline-white focus-visible:outline-offset-[2px]",
  );

  const content = (
    <li className="group w-full">
      {to ? (
        <NavLink to={to} className={commonClasses} onClick={onClick}>
          {({ isActive: navLinkIsActive }) =>
            renderContent(isActive ?? navLinkIsActive)
          }
        </NavLink>
      ) : onClick ? (
        <button type="button" className={commonClasses} onClick={onClick}>
          {renderContent(isActive ?? false)}
        </button>
      ) : (
        <span className={commonClasses}>
          {renderContent(isActive ?? false)}
        </span>
      )}
    </li>
  );

  if (badgeProps) {
    return (
      <Badge {...badgeProps} className={cn("w-full", badgeProps.className)}>
        {content}
      </Badge>
    );
  }

  return content;
};
